////////////////////////////////////////// Read operations ///////////////////////////////////////

import { Environment } from '@util/Environment';
import firebase from 'firebase/compat/app';
import {
  App,
  FirebaseCollection,
  MedicareAccessAttemptLog,
  MedicareAccessSymptomDiary,
  MedicareAccessTrack,
  MedicareAccessUserTrack,
  User,
  UserData,
} from 'hydra-data-types';
import { getMedicareAccessStage } from '../util/MedicareAccessSymptomUtils';

/**
 * Fetch the document reference where user data is stored for a particular product.
 * Nerva: `user_data/<userId>`
 * All other products: `users/<userId>/user_data`
 */
export function userDataDocRef(userId: string): firebase.firestore.DocumentReference<UserData> {
  const firestore = firebase.firestore();

  return Environment.APP === App.Nerva
    ? firestore.collection('user_data').doc(userId)
    : getUserDocRef(userId).collection('user_data').doc(Environment.APP);
}

/** Fetch the document reference for the user collection. */
export function getUserDocRef(userId: string): firebase.firestore.DocumentReference {
  return firebase.firestore().collection('users').doc(userId);
}

export async function fetchUserData(userId: User['userId']): Promise<UserData> {
  try {
    const userDataDoc = await userDataDocRef(userId).get();
    return userDataDoc.data() ?? {};
  } catch (error) {
    if ((error as { code: string })?.code === 'permission-denied') {
      // TODO: This is all a workaround for the fact that we should be calling registerExisting() for the
      // 'new' product. Fix this as part of PRO-3403

      // This can happen if the user has not yet registered for the active product. e.g. a Nerva user is
      // logging in to Evia Web Onboarding for the first time, since they won't have the claims necessary
      // to access the user_data collection.
      // In that case the doc won't exist anyway so just return an empty UserData.
      // More permissions errors will be thrown as the user completes the assessment and attempts to write to
      // UserData. The user will then be sent through in-app onboarding when they first sign in as no UserData is
      // stored from Web Onboarding.
      return {};
    } else {
      throw error;
    }
  }
}

type MedicareAccessTrackParams = {
  userId: User['userId'];
  track: MedicareAccessTrack;
};

/** The Medicare Access track doc (`/users/<userId>/medicare_access/<track>`), which holds the
 * lifecycleStatus written by the backend eligibility workflow. */
export function medicareAccessUserTrackDocRef(
  params: MedicareAccessTrackParams
): firebase.firestore.DocumentReference<MedicareAccessUserTrack> {
  const { userId, track } = params;

  return getUserDocRef(userId)
    .collection(FirebaseCollection.MedicareAccess)
    .doc(track) as firebase.firestore.DocumentReference<MedicareAccessUserTrack>;
}

/** The attempt-logs subcollection for a Medicare Access track
 * (`/users/<userId>/medicare_access/<track>/attempt_logs`), one doc per external API attempt. */
export function medicareAccessAttemptLogsCollectionRef(
  params: MedicareAccessTrackParams
): firebase.firestore.CollectionReference<MedicareAccessAttemptLog> {
  return medicareAccessUserTrackDocRef(params).collection(
    FirebaseCollection.AttemptLogs
  ) as firebase.firestore.CollectionReference<MedicareAccessAttemptLog>;
}

type MedicareAccessSymptomDiaryParams = {
  userId: User['userId'];
  track: MedicareAccessTrack;
};

export function medicareAccessSymptomDiaryDocRef(
  params: MedicareAccessSymptomDiaryParams
): firebase.firestore.DocumentReference<MedicareAccessSymptomDiary> {
  const { userId, track } = params;

  return getUserDocRef(userId)
    .collection(FirebaseCollection.MedicareAccess)
    .doc(track)
    .collection(FirebaseCollection.SymptomDiary)
    .doc(
      getMedicareAccessStage()
    ) as firebase.firestore.DocumentReference<MedicareAccessSymptomDiary>;
}
